// Sprint 22.6 — Room status sheet (replaces "Generate forecast"): print it, or download it as CSV. Built from the live room board.
import React, { useEffect, useMemo } from 'react';
import '../Forecasting/ForecastSheet.css';
import './ForecastDaySheet.css';
import { createPortal } from 'react-dom';
import usePrintSheet from '../shared/usePrintSheet';
import { buildRoomSheet, roomSheetCsv, TONE_LABEL } from './roomSheet';

const fmtDate = (ymd) => {
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

const RoomStatusSheet = ({ payload, live, propertyName = 'Snoqualmie Inn', onClose }) => {
  const sheet = useMemo(() => buildRoomSheet(payload, live), [payload, live]);
  usePrintSheet();
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const download = () => {
    const blob = new Blob([roomSheetCsv(sheet)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob), a = document.createElement('a');
    a.href = url; a.download = `room-status-${sheet.date || 'today'}.csv`; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const asOf = sheet.asOf ? new Date(sheet.asOf).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null;

  const node = (
    <div className="fc-sheet-backdrop fc-print-root" onClick={onClose} role="dialog" aria-modal="true" aria-label="Room status sheet">
      <div className="fc-sheet-wrap" onClick={(e) => e.stopPropagation()}>
        <div className="fc-sheet-controls">
          <div className="fc-sheet-controls-left">
            <span className="fc-sheet-controls-title">Room status sheet · {fmtDate(sheet.date)}</span>
            <span className="fc-sheet-controls-hint">Press Esc or click outside to close</span>
          </div>
          <div className="fc-sheet-controls-right">
            <button className="fc-sheet-btn fc-sheet-btn-primary" onClick={() => window.print()}>▤ Print</button>
            <button className="fc-sheet-btn fc-sheet-btn-secondary" onClick={download}>⇩ Download CSV</button>
            <button className="fc-sheet-btn fc-sheet-btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>

        <article className="fc-sheet rs-sheet">
          <header className="fc-sheet-header">
            <div className="fc-sheet-title-block">
              <h1>Room status</h1>
              <p className="fc-sheet-subtitle">{propertyName} · {fmtDate(sheet.date)}{asOf ? ` · as of ${asOf}` : sheet.source === 'snapshot' ? ' · from the last update' : ''}</p>
            </div>
            <div className="fc-sheet-summary">
              <div><strong>{sheet.totals.available}</strong><span>Available</span></div>
              <div><strong>{sheet.totals.unavailable}</strong><span>Unavailable</span></div>
              <div><strong>{sheet.totals.occupied}</strong><span>Occupied</span></div>
              <div><strong>{sheet.totals.rooms}</strong><span>Rooms</span></div>
            </div>
          </header>

          <div className="rs-legend">{['inspected', 'pickup', 'occupied', 'progress', 'dirty', 'off'].map(k => <span key={k}><i className={`rs-dot rs-dot-${k}`} />{TONE_LABEL[k]}</span>)}</div>

          {sheet.floors.length === 0 && <p className="fc-sheet-empty">No rooms available yet.</p>}
          <div className="rs-floors">
          {sheet.floors.map(f => (
            <section className="fc-sheet-floor" key={f.floorId}>
              <h2>
                <span className="fc-sheet-floor-name">{f.label}</span>
                <span className="fc-sheet-floor-meta">{f.counts.available} available · {f.counts.unavailable} unavailable · {f.counts.occupied} occupied</span>
              </h2>
              <table className="fc-sheet-table rs-table">
                <thead><tr><th>Room</th><th>Type</th><th>Status</th><th>Stay (check-in → check-out)</th></tr></thead>
                <tbody>
                  {f.rooms.map(r => (
                    <tr key={r.roomNumber} className={`rs-row rs-${r.tone}`}>
                      <td className="fc-sheet-room-num">{r.roomNumber}</td>
                      <td>{r.typeLabel || r.typeCode}</td>
                      <td className="fc-sheet-action"><span className={`rs-dot rs-dot-${r.tone}`} />{r.toneLabel}</td>
                      <td>{r.checkout}{r.arrivingToday ? <span className="rs-arr">arriving today</span> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
          </div>

          <footer className="fc-sheet-footer">
            <div>Generated by HotelOps · {new Date().toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
          </footer>
        </article>
      </div>
    </div>
  );
  // Portal to <body>: printing hides every other body child, so nothing blank sits in front of the sheet.
  return createPortal(node, document.body);
};

export default RoomStatusSheet;
